import { css, cx } from "@emotion/css";
import { ExternalLink, DeprecatedIconButton, tableStyles } from "@octopusdeploy/design-system-components";
import { ChevronDownIcon, ChevronRightIcon, ExclamationCircleIcon } from "@octopusdeploy/design-system-icons";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ArgoCDApplicationResource } from "@octopusdeploy/octopus-server-client";
import React, { Fragment } from "react";
import type { ArgoCDInstanceApplicationsPageLoaderData } from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDInstanceApplicationsPage";
import { ArgoCDInstanceLinks } from "~/areas/infrastructure/components/ArgoCDInstances/argoCDInstanceLinks";
import { ArgoCDApplicationDetails } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDApplicationDetails";
import { ArgoCDApplicationEnvironmentsTagList } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDApplicationEnvironmentsTagList";
import { ArgoCDApplicationProjectsTagList } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDApplicationProjectsTagList";
import { ArgoCDApplicationTenantsTagList } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDApplicationTenantsTagList";
import { EmptyStateNoAnnotations } from "~/areas/infrastructure/components/ArgoCDInstances/components/EmptyStateNoAnnotations";
import { isApplicationMissingRequiredConfiguration } from "~/areas/infrastructure/components/ArgoCDInstances/configurationValidation";
const styles = {
    flexRow: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    }),
    nameFlex: css({
        display: "inline-flex",
        flexDirection: "row",
        alignItems: "center",
        gap: space[16],
    }),
    nameTh: css({
        paddingLeft: `${space[72]} !important`,
    }),
    nameTd: css({
        gap: space[32],
    }),
    detailsTd: css({
        padding: "0 !important",
    }),
    tenantsTh: css({
        ":after": {
            border: "0 !important",
        },
    }),
    tenantsTd: css({
        justifyContent: "space-between",
    }),
};
type ArgoCDApplicationsTableProps = ArgoCDInstanceApplicationsPageLoaderData & {
    showAnnotationsDrawer: (selectedApplicationId: string, selectedApplicationSourceIndex: number | undefined) => void;
    onGitCredentialAdded: () => void;
};
function hasAnyOctopusAnnotations(application: ArgoCDApplicationResource) {
    return Object.keys(application.Annotations).some((key) => key.startsWith("argo.octopus.com/"));
}
export const ArgoCDApplicationsTable = (props: ArgoCDApplicationsTableProps) => {
    const links = props.gateway.WebUIUri ? new ArgoCDInstanceLinks(props.gateway.WebUIUri) : undefined;
    return (<table className={tableStyles} aria-label="argo cd applications table">
            <thead>
                <tr>
                    <th className={styles.nameTh}>Argo CD Application</th>
                    <th>Projects</th>
                    <th>Environments</th>
                    <th className={styles.tenantsTh}>Tenants</th>
                </tr>
            </thead>
            <tbody>
                {props.applicationsResponse.Resources.map((app) => {
            const showWarning = hasAnyOctopusAnnotations(app) && isApplicationMissingRequiredConfiguration(app, props.gateway);
            return <ArgoCDApplicationsTableRow key={`application-${app.Name}`} app={app} showWarning={showWarning} links={links} {...props}/>;
        })}
            </tbody>
        </table>);
};
type ArgoCDApplicationsTableRowProps = ArgoCDApplicationsTableProps & {
    app: ArgoCDApplicationResource;
    showWarning: boolean;
    links: ArgoCDInstanceLinks | undefined;
};
const ArgoCDApplicationsTableRow = (props: ArgoCDApplicationsTableRowProps) => {
    const { app, showWarning, links } = props;
    const [isExpanded, setIsExpanded] = React.useState(showWarning);
    return (<Fragment key={app.Name}>
            <tr>
                <td aria-label={"Argo CD Application"}>
                    <div className={cx(styles.flexRow, styles.nameTd)}>
                        <span>
                            <DeprecatedIconButton onClick={() => setIsExpanded(!isExpanded)} customIcon={isExpanded ? <ChevronDownIcon size={20} color={themeTokens.color.icon.primary}/> : <ChevronRightIcon size={20} color={themeTokens.color.icon.primary}/>}/>
                        </span>
                        <span className={styles.nameFlex}>
                            {app.Name}
                            {links && <ExternalLink href={links.applicationDetails(app.Name, app.KubernetesNamespace)}>{""}</ExternalLink>}
                        </span>
                    </div>
                </td>
                <td aria-label={"Projects"}>
                    <ArgoCDApplicationProjectsTagList projects={props.projects} application={app}/>
                </td>
                <td aria-label={"Environments"}>
                    <ArgoCDApplicationEnvironmentsTagList environments={props.environments} application={app}/>
                </td>
                <td aria-label={"Tenants"}>
                    <div className={cx(styles.flexRow, styles.tenantsTd)}>
                        <ArgoCDApplicationTenantsTagList tenants={props.tenants} application={app}/>
                        {showWarning && <ExclamationCircleIcon size={20} color={themeTokens.color.icon.warning}/>}
                    </div>
                </td>
            </tr>
            {isExpanded && (<tr>
                    <td className={styles.detailsTd} colSpan={4}>
                        {hasAnyOctopusAnnotations(app) ? (<ArgoCDApplicationDetails application={app} onGitCredentialAdded={props.onGitCredentialAdded} onGenerateAnnotationsClick={(applicationSourceIndex) => props.showAnnotationsDrawer(app.Id, applicationSourceIndex)} projects={props.projects} environments={props.environments} tenants={props.tenants} gateway={props.gateway}/>) : (<EmptyStateNoAnnotations onGenerateAnnotationsClick={() => props.showAnnotationsDrawer(app.Id, undefined)}/>)}
                    </td>
                </tr>)}
        </Fragment>);
};
